import React from 'react';
import { createRoot } from 'react-dom/client';
import { init, reactErrorHandler } from '@sentry/react';
import { configure } from 'mobx';

import { traceMobxWarnings } from 'utils/traceMobxWarnings';
import { sentryOptions } from 'utils/sentryOptions';

import { App } from './components/App/App';
import { injector } from './Injector';

import { AssignmentApi, WPApi } from 'assignment/api';
import {
  ASSIGNMENT_REPO,
  AssignmentRepo,
  ArticleRepo,
  ARTICLE_REPO_KEY,
  ARTICLE_SERVICE_KEY
} from 'assignment/Assignment';
import { AssignmentService, ASSIGNMENT_SERVICE, ArticleService } from 'assignment/service';
import { SEARCH_SERVICE, SearchService } from 'search/service';
import { SEARCH_REPO, SearchRepo } from 'search/Search';
import { DraftAssignmentRepo, DRAFT_ASSIGNMENT_REPO } from 'assignment/assignmentDraft/AssignmentDraft';
import { DraftAssignmentApi } from 'assignment/assignmentDraft/api';
import { DraftAssignmentService, DRAFT_ASSIGNMENT_SERVICE } from 'assignment/assignmentDraft/service';
import { QuestionaryRepo, QUESTIONARY_REPO } from 'assignment/questionary/Questionary';
import { QuestionaryApi } from 'assignment/questionary/api';
import { QuestionaryService, QUESTIONARY_SERVICE } from 'assignment/questionary/service';
import { UserRepo, USER_REPO } from 'user/User';
import { UserService, USER_SERVICE } from 'user/UserService';
import { UserApi } from 'user/api/UserApi';
import { STORAGE_INTERACTOR_KEY, StorageInteractor } from 'utils/storageInteractor';
import { DISTRIBUTION_REPO, DistributionRepo } from 'distribution/Distribution';
import { DistributionApi } from 'distribution/api';
import { DISTRIBUTION_SERVICE, DistributionService } from 'distribution/service';
import { DRAFT_TEACHING_PATH_REPO, DraftTeachingPathRepo } from 'teachingPath/teachingPathDraft/TeachingPathDraft';
import { DraftTeachingPathApi } from 'teachingPath/teachingPathDraft/api';
import { DRAFT_TEACHING_PATH_SERVICE, DraftTeachingPathService } from 'teachingPath/teachingPathDraft/service';
import { TEACHING_PATH_SERVICE, TeachingPathService } from './teachingPath/service';
import { TEACHING_PATH_REPO, TeachingPathRepo } from './teachingPath/TeachingPath';
import { TeachingPathApi } from './teachingPath/api';
import {
  ASSIGNMENT_EVALUATION_REPO,
  AssignmentEvaluationRepo,
  TEACHING_PATH_EVALUATION_REPO,
  TeachingPathEvaluationRepo,
} from './evaluation/Evaluation';
import {
  ASSIGNMENT_EVALUATION_SERVICE,
  AssignmentEvaluationService,
  TEACHING_PATH_EVALUATION_SERVICE,
  TeachingPathEvaluationService
} from './evaluation/service';
import { AssignmentEvaluationApi, TeachingPathEvaluationApi } from './evaluation/api';
import { StudentRepo, STUDENT_REPO, StudentApi } from 'student/api';
import { STUDENT_SERVICE, StudentService } from 'student/service';
import { SearchApi } from 'search/api';

/**
 * mobx's own default, now that the code satisfies it.
 *
 * 'observed' warns whenever observed state is mutated outside an action. The mobx 6
 * migration pinned mobx 5's permissive 'never' rather than fix that in the same change;
 * the 23 async actions that mutated after an await now wrap those writes in runInAction,
 * so the check can be on.
 *
 * It stays on deliberately. Mutating observed state outside an action is exactly the class
 * of bug concurrent rendering makes unpredictable, and this is the only thing that catches
 * it — the test suite cannot, because mobx only warns about state something is observing
 * and jest attaches almost no observers.
 */
configure({ enforceActions: 'observed' });

/**
 * In development, every [MobX] warning above gets a stack trace attached. The warning alone
 * names the observable and not the code that wrote to it, which is why these accumulate.
 */
traceMobxWarnings();

injector.set<StorageInteractor>(STORAGE_INTERACTOR_KEY, new StorageInteractor());
injector.set<ArticleRepo>(ARTICLE_REPO_KEY, new WPApi());
injector.set<ArticleService>(ARTICLE_SERVICE_KEY, new ArticleService());

injector.set<AssignmentRepo>(ASSIGNMENT_REPO, new AssignmentApi());
injector.set<AssignmentService>(ASSIGNMENT_SERVICE, new AssignmentService());
injector.set<UserRepo>(USER_REPO, new UserApi());
injector.set<UserService>(USER_SERVICE, new UserService());

injector.set<DraftAssignmentRepo>(DRAFT_ASSIGNMENT_REPO, new DraftAssignmentApi());
injector.set<DraftAssignmentService>(DRAFT_ASSIGNMENT_SERVICE, new DraftAssignmentService());

injector.set<QuestionaryRepo>(QUESTIONARY_REPO, new QuestionaryApi());
injector.set<QuestionaryService>(QUESTIONARY_SERVICE, new QuestionaryService());

injector.set<DistributionRepo>(DISTRIBUTION_REPO, new DistributionApi());
injector.set<DistributionService>(DISTRIBUTION_SERVICE, new DistributionService());

injector.set<DraftTeachingPathRepo>(DRAFT_TEACHING_PATH_REPO, new DraftTeachingPathApi());
injector.set<DraftTeachingPathService>(DRAFT_TEACHING_PATH_SERVICE, new DraftTeachingPathService());

injector.set<TeachingPathRepo>(TEACHING_PATH_REPO, new TeachingPathApi());
injector.set<TeachingPathService>(TEACHING_PATH_SERVICE, new TeachingPathService());

injector.set<AssignmentEvaluationRepo>(ASSIGNMENT_EVALUATION_REPO, new AssignmentEvaluationApi());
injector.set<AssignmentEvaluationService>(ASSIGNMENT_EVALUATION_SERVICE, new AssignmentEvaluationService());

injector.set<TeachingPathEvaluationRepo>(TEACHING_PATH_EVALUATION_REPO, new TeachingPathEvaluationApi());
injector.set<TeachingPathEvaluationService>(TEACHING_PATH_EVALUATION_SERVICE, new TeachingPathEvaluationService());

injector.set<StudentRepo>(STUDENT_REPO, new StudentApi());
injector.set<StudentService>(STUDENT_SERVICE, new StudentService());


injector.set<SearchRepo>(SEARCH_REPO, new SearchApi());
injector.set<SearchService>(SEARCH_SERVICE, new SearchService());

if (process.env.REACT_APP_SENTRY_DSN) {
  init(sentryOptions(process.env.REACT_APP_SENTRY_DSN));
}

/**
 * createRoot, not ReactDOM.render. This is what turns React 18 on: concurrent rendering,
 * automatic batching everywhere rather than only inside React event handlers, and — because
 * StrictMode is already enabled — a mount, unmount and remount of every component on first
 * render, which is an 18-only behaviour that the React 17 StrictMode dry run could not
 * exercise.
 *
 * #root is in public/index.handlebars and is always present; the non-null assertion matches
 * what ReactDOM.render assumed anyway.
 */
createRoot(document.getElementById('root')!, {
  /**
   * React 19 no longer re-throws render errors. An uncaught one goes to these handlers and
   * the whole tree is unmounted — a blank page — and a boundary-caught one goes only where
   * the boundary sends it.
   *
   * Sentry's `reactErrorHandler()` reports and returns; **it does not write to the console.**
   * That is why an application that vanished mid-session left a console with nothing in it,
   * and why "the whole app disappeared" was so hard to tell apart from "the page never
   * loaded". The log below is not a nicety: without it the one failure that destroys the
   * entire UI is also the quietest one in it.
   */
  onUncaughtError: (error, errorInfo) => {
    console.error(
      'React unmounted the whole application because this render error reached the root. '
      + 'Nothing above the failing component caught it.',
      error,
      errorInfo.componentStack,
    );
    reactErrorHandler()(error, errorInfo);
  },
  onCaughtError: (error, errorInfo) => {
    console.error('A render error was caught by a boundary.', error, errorInfo.componentStack);
    reactErrorHandler()(error, errorInfo);
  },
}).render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
);
